import axios from 'axios';

/** Prefer REACT_APP_API_BASE; on localhost use local backend so dev matches backend/.env DB. */
function resolveApiBase() {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE;
  }
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'http://localhost:5001/api';
    }
  }
  return 'https://backend-avrnhh.fly.dev/api';
}

const API_BASE = resolveApiBase();

export const api = axios.create({ baseURL: API_BASE });

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export async function register(data) {
    const res = await api.post('/auth/register', data);
    return res.data;
}

export async function login(data) {
    const res = await api.post('/auth/login', data);
    return res.data;
}

export async function checkService(data) {
    const res = await api.post('/service/check', data);
    return res.data;
}

// Products API
export async function getProducts(params = {}) {
    const res = await api.get('/products', { params });
    return res.data;
}

export async function getProduct(id) {
    const res = await api.get(`/products/${id}`);
    return res.data;
}

export async function createProduct(data) {
    const res = await api.post('/products', data);
    return res.data;
}

export async function updateProduct(id, data) {
    const res = await api.put(`/products/${id}`, data);
    return res.data;
}

export async function deleteProduct(id) {
    const res = await api.delete(`/products/${id}`);
    return res.data;
}

// Categories API
export async function getCategories() {
    const res = await api.get('/categories');
    return res.data;
}

export async function getCategory(slug) {
    const res = await api.get(`/categories/${slug}`);
    return res.data;
}

export async function getCategoryById(id) {
    const res = await api.get(`/categories/${id}`);
    return res.data;
}

export async function createCategory(data) {
    const res = await api.post('/categories', data);
    return res.data;
}

export async function updateCategory(id, data) {
    const res = await api.put(`/categories/${id}`, data);
    return res.data;
}

export async function deleteCategory(id) {
    const res = await api.delete(`/categories/${id}`);
    return res.data;
}

// Cart API
export async function getCart() {
    const res = await api.get('/cart');
    return res.data;
}

export async function addToCart(productId, variant, quantity = 1) {
    const res = await api.post('/cart/add', { productId, variant, quantity });
    return res.data;
}

export async function updateCartItem(itemId, quantity) {
    const res = await api.put(`/cart/item/${itemId}`, { quantity });
    return res.data;
}

export async function removeFromCart(itemId) {
    const res = await api.delete(`/cart/item/${itemId}`);
    return res.data;
}

export async function clearCart() {
    const res = await api.delete('/cart/clear');
    return res.data;
}

// Coupons API
export async function getActiveCoupons() {
    const res = await api.get('/coupons/active');
    return res.data;
}

export async function validateCoupon(code, cartTotal) {
    const res = await api.post('/coupons/validate', { code, cartTotal });
    return res.data;
}

// Admin Coupons API
export async function getAllCoupons() {
    const res = await api.get('/coupons');
    return res.data;
}

export async function createCoupon(couponData) {
    const res = await api.post('/coupons', couponData);
    return res.data;
}

export async function updateCoupon(id, couponData) {
    const res = await api.put(`/coupons/${id}`, couponData);
    return res.data;
}

export async function deleteCoupon(id) {
    const res = await api.delete(`/coupons/${id}`);
    return res.data;
}

// User Profile API
export async function getUserProfile() {
    const res = await api.get('/users/me');
    return res.data;
}

export async function updateUserProfile(data) {
    const res = await api.put('/users/me', data);
    return res.data;
}

// Address API
export async function addAddress(addressData) {
    const res = await api.post('/users/me/addresses', addressData);
    return res.data;
}

export async function updateAddress(addressId, addressData) {
    const res = await api.put(`/users/me/addresses/${addressId}`, addressData);
    return res.data;
}

export async function deleteAddress(addressId) {
    const res = await api.delete(`/users/me/addresses/${addressId}`);
    return res.data;
}

// Admin: Get all users
export async function getAllUsers() {
    const res = await api.get('/users/all');
    return res.data;
}

// Orders API
export async function getUserOrders() {
    const res = await api.get('/orders/my');
    return res.data;
}

export async function getOrder(id) {
    const res = await api.get(`/orders/${id}`);
    return res.data;
}

// Admin Orders API
export async function getAllOrders(status = null) {
    const url = status ? `/orders/admin/all?status=${status}` : '/orders/admin/all';
    const res = await api.get(url);
    return res.data;
}

export async function updateOrderStatus(orderId, status) {
    const res = await api.put(`/orders/${orderId}/status`, { status });
    return res.data;
}

// Units API
export async function getUnits() {
    const res = await api.get('/units');
    return res.data;
}

export async function createUnit(data) {
    const res = await api.post('/units', data);
    return res.data;
}

export async function updateUnit(id, data) {
    const res = await api.put(`/units/${id}`, data);
    return res.data;
}

export async function deleteUnit(id) {
    const res = await api.delete(`/units/${id}`);
    return res.data;
}

export async function placeOrder(orderData) {
    const res = await api.post('/orders', orderData);
    return res.data;
}

// OTP API
export async function sendPhoneOTP(phone) {
    const res = await api.post('/auth/send-phone-otp', { phone });
    return res.data;
}

export async function verifyPhoneOTP(phone, otp) {
    const res = await api.post('/auth/verify-phone-otp', { phone, otp });
    return res.data;
}

// Image Upload API
export async function uploadImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    // Use a separate axios instance without the base interceptor for file uploads
    // This ensures FormData is handled correctly with proper Content-Type boundary
    const uploadApi = axios.create({ 
        baseURL: API_BASE,
        headers: {
            // Don't set Content-Type - browser will set it with boundary for FormData
        }
    });
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
        uploadApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Note: API_BASE already includes '/api', so we use '/upload/image' not '/api/upload/image'
    const res = await uploadApi.post('/upload/image', formData);
    return res.data;
}